'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { FaceLandmarker, HandLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { createClient } from '@/lib/supabase/client';

interface LiveVisualWellnessCheckProps {
  contactId: string;
  onSave: () => void;
}

const FACE_OVAL_INDICES = [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109];
const LEFT_EYE_INDICES = [33, 246, 161, 160, 159, 158, 157, 173, 133, 155, 154, 153, 145, 144, 163, 7];
const RIGHT_EYE_INDICES = [362, 398, 384, 385, 386, 387, 388, 466, 263, 249, 390, 373, 374, 380, 381, 382];
const MOUTH_INDICES = [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146];
const NOSE_BRIDGE_INDICES = [6, 197, 195, 5, 4];
const NOSE_TIP_INDEX = 4;
const CENTER_X_INDEX = 1;

const SCORE_UPDATE_MS = 500;
const TREMBER_HISTORY_LEN = 10;

type Lm = { x: number; y: number; z?: number };

function getScoreColor(score: number) {
  if (score >= 70) return 'text-emerald-500';
  if (score >= 50) return 'text-amber-500';
  return 'text-red-500';
}

function getBarColor(score: number) {
  if (score >= 70) return 'bg-emerald-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function getStatusText(score: number) {
  if (score >= 70) return 'Normal';
  if (score >= 50) return 'Mild concern';
  return 'Alert';
}

const LiveVisualWellnessCheck = ({ contactId, onSave }: LiveVisualWellnessCheckProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [assessing, setAssessing] = useState(false);
  const [countdown, setCountdown] = useState(15);
  const [currentStep, setCurrentStep] = useState('');
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportSent, setReportSent] = useState(false);
  const [pendingStream, setPendingStream] = useState<MediaStream | null>(null);
  const [scores, setScores] = useState({
    fatigue: 0,
    tremor: 100,
    symmetry: 100,
    alertness: 100,
    overall: 0,
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const faceLandmarkerRef = useRef<FaceLandmarker | null>(null);
  const handLandmarkerRef = useRef<HandLandmarker | null>(null);
  const animationRef = useRef<number | null>(null);
  const landmarkHistoryRef = useRef<{ x: number; y: number }[]>([]);
  const lastScoresUpdateRef = useRef(0);
  const scoresAccRef = useRef({ fatigue: 0, tremor: 100, symmetry: 100, alertness: 100, n: 0 });
  const ovalOffsetRef = useRef(0);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    if (faceLandmarkerRef.current) {
      faceLandmarkerRef.current.close();
      faceLandmarkerRef.current = null;
    }
    if (handLandmarkerRef.current) {
      handLandmarkerRef.current.close();
      handLandmarkerRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Set video.srcObject after video element is mounted (fixes black video)
  useEffect(() => {
    if (!pendingStream || !videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    video.srcObject = pendingStream;
    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      setCameraActive(true);
      setPendingStream(null);
    };
    return () => {
      setPendingStream(null);
    };
  }, [pendingStream]);

  const startCamera = async () => {
    try {
      setError(null);
      setReportSent(false);
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm'
      );
      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
          delegate: 'GPU',
        },
        outputFaceBlendshapes: true,
        runningMode: 'VIDEO',
        numFaces: 1,
      });
      handLandmarkerRef.current = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        numHands: 2,
      });
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      setPendingStream(stream);
    } catch (err: unknown) {
      console.error(err);
      setError('Failed to access camera. Please ensure camera permissions are granted.');
    }
  };

  const runAssessment = useCallback(() => {
    setAssessing(true);
    setCountdown(15);
    landmarkHistoryRef.current = [];
    scoresAccRef.current = { fatigue: 0, tremor: 100, symmetry: 100, alertness: 100, n: 0 };
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    const steps = [
      { time: 0, text: 'Detecting face landmarks...' },
      { time: 4000, text: 'Analyzing posture alignment...' },
      { time: 8000, text: 'Measuring stability...' },
      { time: 12000, text: 'Computing wellness scores...' },
    ];
    steps.forEach((s) => setTimeout(() => setCurrentStep(s.text), s.time));
    setTimeout(() => {
      clearInterval(countdownInterval);
      finishAssessment();
    }, 15000);
  }, []);

  const finishAssessment = useCallback(() => {
    const acc = scoresAccRef.current;
    const n = acc.n || 1;
    const fatigue = Math.min(100, Math.max(0, Math.round(acc.fatigue / n)));
    const tremor = Math.min(100, Math.max(0, Math.round(acc.tremor / n)));
    const symmetry = Math.min(100, Math.max(0, Math.round(acc.symmetry / n)));
    const alertness = Math.min(100, Math.max(0, Math.round(acc.alertness / n)));
    const overall = Math.min(100, Math.max(0, Math.round((fatigue + tremor + symmetry + alertness) / 4)));
    setResults({
      overallScore: overall,
      tremor: { score: tremor, status: getStatusText(tremor) },
      posture: { score: alertness, status: getStatusText(alertness) },
      fatigue: { score: fatigue, status: getStatusText(fatigue) },
      symmetry: { score: symmetry, status: getStatusText(symmetry) },
      fallRisk: overall >= 80 ? 'LOW' : overall >= 60 ? 'MODERATE' : 'HIGH',
    });
    setAssessing(false);
    stopCamera();
  }, [stopCamera]);

  const detectLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const faceLandmarker = faceLandmarkerRef.current;
    const handLandmarker = handLandmarkerRef.current;
    if (!video || !canvas || !faceLandmarker || !assessing) return;
    if (video.readyState < 2) {
      animationRef.current = requestAnimationFrame(detectLoop);
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      animationRef.current = requestAnimationFrame(detectLoop);
      return;
    }
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    const timestamp = performance.now();
    const faceResults = faceLandmarker.detectForVideo(video, timestamp) as {
      faceLandmarks?: Lm[][];
      faceBlendshapes?: Array<{ categories: Array<{ categoryName: string; score: number }> }>;
    };
    let fatigueScore = 0;
    let symmetryScore = 100;
    let alertnessScore = 100;
    let tremorScore = 100;
    let centerX = 0.5;
    let asymmetryDetected = false;

    if (faceResults.faceLandmarks && faceResults.faceLandmarks.length > 0) {
      const landmarks = faceResults.faceLandmarks[0];
      const blendshapes = faceResults.faceBlendshapes?.[0]?.categories ?? [];
      const leftBlink = blendshapes.find((b) => b.categoryName === 'eyeBlinkLeft')?.score ?? 0;
      const rightBlink = blendshapes.find((b) => b.categoryName === 'eyeBlinkRight')?.score ?? 0;
      const eyeOpenness = 100 - (leftBlink + rightBlink) * 50;
      fatigueScore = Math.min(100, Math.max(0, Math.round(eyeOpenness)));
      const ear = eyeOpenness / 100;
      const fatigueGood = ear >= 0.2;

      const leftSmile = blendshapes.find((b) => b.categoryName === 'mouthSmileLeft')?.score ?? 0;
      const rightSmile = blendshapes.find((b) => b.categoryName === 'mouthSmileRight')?.score ?? 0;
      const asym = Math.abs(leftSmile - rightSmile) * 200;
      symmetryScore = Math.min(100, Math.max(0, 100 - Math.round(asym)));
      asymmetryDetected = symmetryScore < 85;

      const leftEyeY = LEFT_EYE_INDICES.reduce((s, i) => s + (landmarks[i]?.y ?? 0), 0) / LEFT_EYE_INDICES.length;
      const rightEyeY = RIGHT_EYE_INDICES.reduce((s, i) => s + (landmarks[i]?.y ?? 0), 0) / RIGHT_EYE_INDICES.length;
      const eyeCenterY = (leftEyeY + rightEyeY) / 2;
      const noseY = landmarks[NOSE_TIP_INDEX]?.y ?? 0.5;
      const headDroop = noseY - eyeCenterY;
      alertnessScore = Math.min(100, Math.max(0, Math.round(100 - Math.max(0, headDroop - 0.02) * 800)));

      const noseTip = landmarks[NOSE_TIP_INDEX];
      if (noseTip) {
        landmarkHistoryRef.current.push({ x: noseTip.x, y: noseTip.y });
        if (landmarkHistoryRef.current.length > TREMBER_HISTORY_LEN) landmarkHistoryRef.current.shift();
      }
      if (landmarkHistoryRef.current.length >= 5) {
        const xs = landmarkHistoryRef.current.map((p) => p.x);
        const ys = landmarkHistoryRef.current.map((p) => p.y);
        const meanX = xs.reduce((a, b) => a + b, 0) / xs.length;
        const meanY = ys.reduce((a, b) => a + b, 0) / ys.length;
        const variance = (xs.reduce((s, x) => s + (x - meanX) ** 2, 0) + ys.reduce((s, y) => s + (y - meanY) ** 2, 0)) / xs.length;
        tremorScore = Math.min(100, Math.max(0, 100 - Math.min(100, variance * 5000)));
      }

      if (handLandmarker && video.readyState >= 2) {
        try {
          const handResults = handLandmarker.detectForVideo(video, timestamp) as { landmarks?: Lm[][] };
          if (handResults.landmarks?.[0]?.[8]) {
            const tip = handResults.landmarks[0][8];
            landmarkHistoryRef.current.push({ x: tip.x, y: tip.y });
            if (landmarkHistoryRef.current.length > TREMBER_HISTORY_LEN) landmarkHistoryRef.current.shift();
            if (landmarkHistoryRef.current.length >= 5) {
              const xs = landmarkHistoryRef.current.map((p) => p.x);
              const ys = landmarkHistoryRef.current.map((p) => p.y);
              const meanX = xs.reduce((a, b) => a + b, 0) / xs.length;
              const meanY = ys.reduce((a, b) => a + b, 0) / ys.length;
              const variance = (xs.reduce((s, x) => s + (x - meanX) ** 2, 0) + ys.reduce((s, y) => s + (y - meanY) ** 2, 0)) / xs.length;
              tremorScore = Math.min(100, Math.max(0, 100 - Math.min(100, variance * 8000)));
            }
          }
        } catch (_) {}
      }

      centerX = landmarks[CENTER_X_INDEX]?.x ?? 0.5;
      const overallFrame = Math.min(100, Math.max(0, (fatigueScore + tremorScore + symmetryScore + alertnessScore) / 4));
      const dotGood = overallFrame >= 50;

      ctx.shadowBlur = 8;
      landmarks.forEach((lm, i) => {
        const x = lm.x * w;
        const y = lm.y * h;
        const isLeft = lm.x < centerX;
        const symBad = asymmetryDetected && (isLeft || lm.x > centerX);
        const concerning = symBad || !fatigueGood || overallFrame < 50;
        ctx.fillStyle = concerning ? '#ef4444' : '#22c55e';
        ctx.shadowColor = concerning ? '#ef4444' : '#22c55e';
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, 2 * Math.PI);
        ctx.fill();
      });
      ctx.shadowBlur = 0;

      ctx.strokeStyle = asymmetryDetected ? '#ef4444' : '#ffffff';
      ctx.lineWidth = 2;
      ctx.shadowBlur = 6;
      ctx.shadowColor = asymmetryDetected ? '#ef4444' : '#22c55e';
      ctx.beginPath();
      ctx.moveTo(centerX * w, 0);
      ctx.lineTo(centerX * w, h);
      ctx.stroke();
      ctx.shadowBlur = 0;

      ovalOffsetRef.current = (ovalOffsetRef.current + 2) % 40;
      ctx.setLineDash([8, 8]);
      ctx.lineDashOffset = -ovalOffsetRef.current;
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 2;
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#22c55e';
      ctx.beginPath();
      for (let i = 0; i < FACE_OVAL_INDICES.length; i++) {
        const idx = FACE_OVAL_INDICES[i];
        const next = FACE_OVAL_INDICES[(i + 1) % FACE_OVAL_INDICES.length];
        const pt = landmarks[idx];
        const pt2 = landmarks[next];
        if (pt && pt2) {
          if (i === 0) ctx.moveTo(pt.x * w, pt.y * h);
          ctx.lineTo(pt2.x * w, pt2.y * h);
        }
      }
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;

      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 3;
      ctx.shadowBlur = 8;
      ctx.shadowColor = '#22c55e';
      ctx.fillStyle = '#22c55e';
      ;[LEFT_EYE_INDICES, RIGHT_EYE_INDICES].forEach((indices) => {
        if (indices.length < 2) return;
        const first = landmarks[indices[0]];
        if (!first) return;
        ctx.beginPath();
        ctx.moveTo(first.x * w, first.y * h);
        for (let i = 1; i < indices.length; i++) {
          const lm = landmarks[indices[i]];
          if (lm) ctx.lineTo(lm.x * w, lm.y * h);
        }
        ctx.closePath();
        ctx.stroke();
        ctx.fill('evenodd');
      });
      MOUTH_INDICES.forEach((i) => {
        const lm = landmarks[i];
        if (lm) {
          ctx.beginPath();
          ctx.arc(lm.x * w, lm.y * h, 2, 0, 2 * Math.PI);
          ctx.fill();
        }
      });
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < NOSE_BRIDGE_INDICES.length - 1; i++) {
        const a = landmarks[NOSE_BRIDGE_INDICES[i]];
        const b = landmarks[NOSE_BRIDGE_INDICES[i + 1]];
        if (a && b) {
          ctx.moveTo(a.x * w, a.y * h);
          ctx.lineTo(b.x * w, b.y * h);
        }
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      const now = Date.now();
      if (now - lastScoresUpdateRef.current >= SCORE_UPDATE_MS) {
        lastScoresUpdateRef.current = now;
        scoresAccRef.current.fatigue += fatigueScore;
        scoresAccRef.current.tremor += tremorScore;
        scoresAccRef.current.symmetry += symmetryScore;
        scoresAccRef.current.alertness += alertnessScore;
        scoresAccRef.current.n += 1;
        const n = scoresAccRef.current.n;
        setScores({
          fatigue: Math.min(100, Math.max(0, Math.round(scoresAccRef.current.fatigue / n))),
          tremor: Math.min(100, Math.max(0, Math.round(scoresAccRef.current.tremor / n))),
          symmetry: Math.min(100, Math.max(0, Math.round(scoresAccRef.current.symmetry / n))),
          alertness: Math.min(100, Math.max(0, Math.round(scoresAccRef.current.alertness / n))),
          overall: Math.min(100, Math.max(0, Math.round(
            (scoresAccRef.current.fatigue + scoresAccRef.current.tremor + scoresAccRef.current.symmetry + scoresAccRef.current.alertness) / 4 / n
          ))),
        });
      }
    }

    if (assessing) animationRef.current = requestAnimationFrame(detectLoop);
  }, [assessing]);

  useEffect(() => {
    if (assessing && cameraActive) detectLoop();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [assessing, cameraActive, detectLoop]);

  const startAssessment = async () => {
    await startCamera();
    setModalOpen(true);
    setTimeout(() => runAssessment(), 800);
  };

  const endAndSendReport = async () => {
    const acc = scoresAccRef.current;
    const n = acc.n || 1;
    const payload = {
      fatigue: Math.min(100, Math.max(0, Math.round(acc.fatigue / n))),
      tremor: Math.min(100, Math.max(0, Math.round(acc.tremor / n))),
      symmetry: Math.min(100, Math.max(0, Math.round(acc.symmetry / n))),
      alertness: Math.min(100, Math.max(0, Math.round(acc.alertness / n))),
      overall: Math.min(100, Math.max(0, Math.round((acc.fatigue + acc.tremor + acc.symmetry + acc.alertness) / 4 / n))),
    };
    stopCamera();
    setAssessing(false);
    setResults({
      overallScore: payload.overall,
      tremor: { score: payload.tremor, status: getStatusText(payload.tremor) },
      posture: { score: payload.alertness, status: getStatusText(payload.alertness) },
      fatigue: { score: payload.fatigue, status: getStatusText(payload.fatigue) },
      symmetry: { score: payload.symmetry, status: getStatusText(payload.symmetry) },
      fallRisk: payload.overall >= 80 ? 'LOW' : payload.overall >= 60 ? 'MODERATE' : 'HIGH',
    });
    try {
      const res = await fetch('/api/twilio/sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores: payload }),
      });
      const data = await res.json();
      if (data.success) setReportSent(true);
    } catch (_) {}
  };

  const sendToCaregiver = async () => {
    if (!results) return;
    const payload = {
      fatigue: Math.min(100, Math.max(0, results.fatigue?.score ?? 0)),
      tremor: Math.min(100, Math.max(0, results.tremor?.score ?? 0)),
      symmetry: Math.min(100, Math.max(0, results.symmetry?.score ?? 0)),
      alertness: Math.min(100, Math.max(0, results.posture?.score ?? 0)),
      overall: Math.min(100, Math.max(0, results.overallScore ?? 0)),
    };
    try {
      const res = await fetch('/api/twilio/sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores: payload }),
      });
      const data = await res.json();
      if (data.success) setReportSent(true);
    } catch (_) {}
  };

  const saveResults = async () => {
    if (!results) return;
    const supabase = createClient();
    try {
      await supabase.from('visual_assessment_results').insert({
        contact_id: contactId,
        assessment_type: 'combined',
        eye_openness_score: results.fatigue?.score ?? 0,
        facial_symmetry_score: results.symmetry?.score ?? 0,
        expression_classification: 'neutral',
        fatigue_indicator: (results.fatigue?.score ?? 0) < 70,
        stroke_indicator: (results.symmetry?.score ?? 0) < 70,
        forward_lean_angle: 100 - (results.posture?.score ?? 0),
        movement_symmetry_score: results.symmetry?.score ?? 0,
        fall_risk_score: results.fallRisk?.toLowerCase() ?? 'low',
        fall_risk_indicator: results.fallRisk !== 'LOW',
        tremor_variance: (100 - (results.tremor?.score ?? 0)) / 100,
        tremor_score: results.tremor?.score ?? 0,
        tremor_detected: (results.tremor?.score ?? 0) < 80,
      });
      setModalOpen(false);
      setResults(null);
      setReportSent(false);
      onSave();
    } catch (e) {
      console.error(e);
    }
  };

  const closeModal = () => {
    stopCamera();
    setModalOpen(false);
    setResults(null);
    setAssessing(false);
    setError(null);
    setReportSent(false);
  };

  return (
    <>
      <div className="glass p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="p-3 rounded-lg bg-secondary/10">
            <Icon name="VideoCameraIcon" size={32} className="text-secondary" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">Visual Wellness Assessment</h2>
            <p className="text-muted-foreground">AI-powered posture, tremor, and fatigue detection using your camera</p>
          </div>
        </div>
        <button
          onClick={startAssessment}
          className="w-full md:w-auto px-8 py-4 bg-primary text-white rounded-lg font-semibold text-lg hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
        >
          <Icon name="PlayIcon" size={24} />
          Start Visual Assessment
        </button>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl border border-gray-700">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
              <h3 className="text-xl font-heading font-semibold text-white">
                {results ? 'Assessment Results' : 'Visual Wellness Assessment'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-white p-1">
                <Icon name="XMarkIcon" size={24} />
              </button>
            </div>

            <div className="p-4 flex-1 flex flex-col md:flex-row gap-4 min-h-0">
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-300 text-sm">{error}</div>
              )}

              {!results && !error && (
                <>
                  <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
                    <div className="flex-1 rounded-xl overflow-hidden bg-black min-h-[280px] aspect-video" style={{ position: 'relative' }}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ position: 'relative', width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                      />
                      <canvas
                        ref={canvasRef}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                          zIndex: 10,
                        }}
                      />
                      <div className="absolute top-3 right-3 flex items-center gap-2">
                        <span
                          className={`w-4 h-4 rounded-full border-2 border-white ${
                            scores.overall >= 80 ? 'bg-emerald-500' : scores.overall >= 50 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                        />
                        <span className="text-2xl font-bold text-white drop-shadow">{scores.overall}</span>
                      </div>
                    </div>
                    <aside className="w-full md:w-56 flex-shrink-0 space-y-3">
                      <div className="rounded-lg bg-gray-800/80 p-3 border border-gray-700">
                        <p className="text-xs text-gray-400 mb-1">Fatigue (EAR)</p>
                        <p className={`text-lg font-bold ${getScoreColor(scores.fatigue)}`}>{scores.fatigue}</p>
                        <div className="h-1.5 rounded-full bg-gray-700 overflow-hidden mt-1">
                          <div className={`h-full rounded-full ${getBarColor(scores.fatigue)}`} style={{ width: `${scores.fatigue}%` }} />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{getStatusText(scores.fatigue)}</p>
                      </div>
                      <div className="rounded-lg bg-gray-800/80 p-3 border border-gray-700">
                        <p className="text-xs text-gray-400 mb-1">Tremor</p>
                        <p className={`text-lg font-bold ${getScoreColor(scores.tremor)}`}>{scores.tremor}</p>
                        <div className="h-1.5 rounded-full bg-gray-700 overflow-hidden mt-1">
                          <div className={`h-full rounded-full ${getBarColor(scores.tremor)}`} style={{ width: `${scores.tremor}%` }} />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{getStatusText(scores.tremor)}</p>
                      </div>
                      <div className="rounded-lg bg-gray-800/80 p-3 border border-gray-700">
                        <p className="text-xs text-gray-400 mb-1">Symmetry</p>
                        <p className={`text-lg font-bold ${getScoreColor(scores.symmetry)}`}>{scores.symmetry}</p>
                        <div className="h-1.5 rounded-full bg-gray-700 overflow-hidden mt-1">
                          <div className={`h-full rounded-full ${getBarColor(scores.symmetry)}`} style={{ width: `${scores.symmetry}%` }} />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{getStatusText(scores.symmetry)}</p>
                      </div>
                      <div className="rounded-lg bg-gray-800/80 p-3 border border-gray-700">
                        <p className="text-xs text-gray-400 mb-1">Alertness</p>
                        <p className={`text-lg font-bold ${getScoreColor(scores.alertness)}`}>{scores.alertness}</p>
                        <div className="h-1.5 rounded-full bg-gray-700 overflow-hidden mt-1">
                          <div className={`h-full rounded-full ${getBarColor(scores.alertness)}`} style={{ width: `${scores.alertness}%` }} />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{getStatusText(scores.alertness)}</p>
                      </div>
                    </aside>
                  </div>
                  {assessing && (
                    <div className="text-center py-2">
                      <span className="text-2xl font-bold text-emerald-400">{countdown}</span>
                      <span className="text-gray-400 ml-2">seconds remaining</span>
                      <p className="text-sm text-gray-500 mt-1">{currentStep}</p>
                    </div>
                  )}
                  <div className="flex gap-3 flex-shrink-0 pt-2">
                    <button
                      onClick={endAndSendReport}
                      className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                    >
                      End Assessment & Send Report
                    </button>
                  </div>
                </>
              )}

              {results && (
                <div className="space-y-4 flex-1 overflow-y-auto">
                  <div className="text-center rounded-xl bg-gray-800 p-6 border border-gray-700">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full border-4 ${
                        results.overallScore >= 80 ? 'border-emerald-500 bg-emerald-500/20' : results.overallScore >= 50 ? 'border-amber-500 bg-amber-500/20' : 'border-red-500 bg-red-500/20'
                      }`}
                    >
                      <span className="text-3xl font-bold text-white">{results.overallScore}</span>
                    </div>
                    <p className="text-gray-300 mt-2">Overall Score</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Tremor', data: results.tremor },
                      { label: 'Posture', data: results.posture },
                      { label: 'Fatigue', data: results.fatigue },
                      { label: 'Symmetry', data: results.symmetry },
                    ].map(({ label, data }) => (
                      <div key={label} className="rounded-lg bg-gray-800/80 p-3 border border-gray-700">
                        <p className="text-xs text-gray-400">{label}</p>
                        <p className="text-xl font-bold text-white">{data?.score ?? 0}</p>
                        <p className="text-xs text-gray-500">{data?.status ?? '—'}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={sendToCaregiver}
                      disabled={reportSent}
                      className="px-6 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 disabled:opacity-60"
                    >
                      {reportSent ? '✓ Report sent to Sarah' : 'Send to Caregiver'}
                    </button>
                    <button onClick={saveResults} className="px-6 py-3 rounded-xl border border-gray-600 text-gray-200 font-semibold hover:bg-gray-800">
                      Save to Today&apos;s Check-In
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveVisualWellnessCheck;
